import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import PlusIcon from 'icons/Plus.icon';
import FilePreviewComp from 'components/FilePreview';
import { MoreDropdown } from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/MoreDropdown';
import MessageFilePreviewModal from '../MessageFilePreviewModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import TrashIcon from 'icons/Trash.icon';
import { AxiosResponse } from 'axios';
import { FilePreview } from 'models/File';
import useCallbackAsync from 'hooks/useCallbackAsync';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import mixpanelService from 'services/mixpanelService';
import { DocumentPublishedEventProps } from 'models/MixpanelEvents';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getHasElementsInArray, getIsOwner } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';

import './MessageFile.styles.scss';

export type MessageFileHandlers = {
  onFilePreview: (
    fileId: string,
    id?: string,
  ) => Promise<AxiosResponse<FilePreview>>;
  onFileAddToDataRoom?: (fileId: string, id?: string) => Promise<void>;
  onFileRemove?: (
    fileId: string,
    removeFromDataRoom?: boolean,
    id?: string,
  ) => Promise<void>;
};

type MessageFileProps = {
  className?: string;
  id: string;
  name: string;
  disabled?: boolean;
  isDataRoomFile?: boolean;
  canMoveFileToDataRoom?: boolean;
  canRemoveFile?: boolean;
  dataRoomId: string;
  dataRoomName: string;
  ownerId?: string;
  userSide?: QAndASides;
} & FilePreview &
  MessageFileHandlers;

const MessageFile: React.FC<MessageFileProps> = (props) => {
  const {
    className,
    id,
    name,
    isDataRoomFile,
    type,
    url,
    disabled,
    canMoveFileToDataRoom,
    canRemoveFile,
    dataRoomName,
    dataRoomId,
    ownerId,
    userSide,
    onFilePreview,
    onFileAddToDataRoom,
    onFileRemove,
  } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { popSuccess } = useContext(PopNotificationsContext);
  const {
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const isOwner = useMemo(
    () => getIsOwner(ownerId, currentUserId),
    [currentUserId, ownerId],
  );

  const remove = useCallback(
    async (fromDataRoom: boolean) => {
      await onFileRemove(id, fromDataRoom);
      popSuccess(t('fileSuccessfullyRemoved'));
    },
    [id, onFileRemove, popSuccess, t],
  );

  const [removeFile, loadingRemove] = useCallbackAsync(async () => {
    if (!isDataRoomFile) {
      await remove(false);
      return;
    }

    if (
      await confirm(
        {
          title: t('removingFromDataRoom'),
          content: t('wouldYouLikeToRemoveFileFromDR'),
          type: 'delete',
          onClose: async () => await remove(false),
        },
        true,
      )
    )
      await remove(true);
  });

  const [saveFileToDataRoom, loadingAdd] = useCallbackAsync(async () => {
    mixpanelService.documentPublished(
      getMixpanelProps({
        dataRoomId,
        dataRoomName,
        documentName: name,
        documentType: type,
        isOwner,
        userSide,
      }) as DocumentPublishedEventProps,
    );

    await onFileAddToDataRoom(id);
  });

  const isDisabled = disabled || loadingAdd || loadingRemove;

  const classes = classNames(
    'message-file',
    { disabled: isDisabled },
    className,
  );

  const dropdownOptions = useMemo(
    () => [
      ...(!isDataRoomFile && canMoveFileToDataRoom && onFileAddToDataRoom
        ? [
            {
              label: t('saveToDR'),
              disabled: isDisabled,
              icon: PlusIcon,
              onClick: saveFileToDataRoom,
            },
          ]
        : []),
      ...(canRemoveFile && onFileRemove
        ? [
            {
              label: t('removeFile'),
              disabled: isDisabled,
              alert: true,
              icon: TrashIcon,
              onClick: removeFile,
            },
          ]
        : []),
    ],
    [
      isDisabled,
      isDataRoomFile,
      canMoveFileToDataRoom,
      canRemoveFile,
      t,
      saveFileToDataRoom,
      onFileAddToDataRoom,
      onFileRemove,
      removeFile,
    ],
  );

  return (
    <div className={classes}>
      <div
        className={classNames({ clickable: !isDisabled })}
        onClick={() => {
          if (isDisabled) return;
          modal?.open();
        }}
      >
        <FilePreviewComp url={url} type={type} />

        <div className="name">
          <p>{name}</p>
        </div>

        {isDataRoomFile && <p className="tag">{t('dr')}</p>}
      </div>

      {getHasElementsInArray(dropdownOptions) && (
        <MoreDropdown options={dropdownOptions} />
      )}

      <MessageFilePreviewModal
        ref={modalRef}
        fileId={id}
        onFilePreview={onFilePreview}
      />
    </div>
  );
};

export default MessageFile;
